import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/ui/Button'
import { Card, CardContent } from '../components/ui/Card'
import { MainNavigation } from '../components/navigation/MainNavigation'
import { MainFooter } from '../components/navigation/MainFooter'
import { useAuthStore } from '../stores/authStore'
import { ServiceConfigUtils } from '../config/services.utils'
import { NAVIGATION_ACTIONS } from '../config/navigation.config'
import { 
  Brain, 
  Zap, 
  Users,
  TrendingUp,
  Shield,
  CheckCircle,
  Target,
  ArrowRight,
  Play,
  Calculator,
  Star,
  ChevronDown,
  ChevronUp,
  Clock,
  DollarSign,
  Award,
  BarChart3,
  Sparkles,
  MessageSquare,
  Building2,
  Rocket,
  AlertTriangle,
  Check,
  X,
  Timer,
  Globe,
  FileText,
  Video,
  Phone,
  Mail,
  Bell,
  Database
} from 'lucide-react'

// Animated counter hook
const useAnimatedCounter = (end: number, duration: number = 2000, startOnView: boolean = true) => {
  const [count, setCount] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!startOnView) {
      animateCount()
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true)
          animateCount()
        }
      },
      { threshold: 0.1 }
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  const animateCount = () => {
    const startTime = Date.now()
    const timer = setInterval(() => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      setCount(Math.floor(progress * end))
      if (progress === 1) clearInterval(timer)
    }, 16)
  }

  return { count, ref }
}

const formatPriceWithSmallDecimals = (priceDisplay: string) => {
  // Split price like "$49.00 USD" into parts
  const match = priceDisplay.match(/^(\$\d+)(\.00)(\s+\w+)$/)
  if (!match) return priceDisplay // Return as-is if format doesn't match

  const [, dollars, cents, currency] = match
  return { dollars, cents, currency }
}

const getTierBadgeConfig = (tierLevel?: string) => {
  switch (tierLevel) {
    case 'quick':
      return {
        label: 'QUICK',
        bgClass: 'bg-gradient-to-r from-emerald-500 to-green-500',
        icon: <Zap className="h-3 w-3" />
      }
    case 'standard':
      return {
        label: 'STANDARD',
        bgClass: 'bg-gradient-to-r from-blue-500 to-cyan-500',
        icon: <BarChart3 className="h-3 w-3" />
      }
    case 'comprehensive':
      return {
        label: 'COMPREHENSIVE',
        bgClass: 'bg-gradient-to-r from-violet-500 to-purple-500',
        icon: <Target className="h-3 w-3" />
      }
    case 'enterprise':
      return {
        label: 'ENTERPRISE',
        bgClass: 'bg-gradient-to-r from-amber-500 to-orange-500',
        icon: <Award className="h-3 w-3" />
      }
    default:
      return null
  }
}

export const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null)
  const [activeTestimonial, setActiveTestimonial] = useState(0)
  const [showVideo, setShowVideo] = useState(false)

  const handleGetStarted = () => {
    navigate(NAVIGATION_ACTIONS.primary.path)
  }

  const handleCalculateROI = () => {
    navigate('/roi-calculator')
  }

  const handleViewPricing = () => {
    navigate('/pricing')
  }

  const handleWatchDemo = () => {
    setShowVideo(true)
  }

  // Animated stats
  const stat1 = useAnimatedCounter(4, 1500)
  const stat2 = useAnimatedCounter(49, 1500)
  const stat3 = useAnimatedCounter(427, 2000)
  const stat4 = useAnimatedCounter(327, 2000)

  const stats = [
    { number: stat1.count, suffix: '-Hour', label: 'Delivery', ref: stat1.ref, icon: <Clock className="h-8 w-8" />, color: 'from-green-500 to-emerald-500' },
    { number: stat2.count, prefix: '$', suffix: '', label: 'Starting', ref: stat2.ref, icon: <DollarSign className="h-8 w-8" />, color: 'from-blue-500 to-cyan-500' },
    { number: stat3.count, suffix: '+', label: 'Data Sources', ref: stat3.ref, icon: <Database className="h-8 w-8" />, color: 'from-purple-500 to-pink-500' },
    { number: stat4.count, suffix: '%', label: 'Average ROI', ref: stat4.ref, icon: <TrendingUp className="h-8 w-8" />, color: 'from-orange-500 to-red-500' }
  ]

  // Client logos (you can replace with actual logos)
  const clientLogos = [
    { name: 'TechCorp', industry: 'Technology' },
    { name: 'HealthPlus', industry: 'Healthcare' },
    { name: 'FinanceHub', industry: 'Financial' },
    { name: 'RetailMax', industry: 'Retail' },
    { name: 'ManuPro', industry: 'Manufacturing' },
    { name: 'AutoDrive', industry: 'Automotive' }
  ]

  // Testimonials
  const testimonials = [
    {
      quote: "StratIQX replaced our $400K annual consulting spend. We now get better insights, faster, at 5% of the cost. The ROI was immediate.",
      author: "Michael Chen",
      title: "CEO, TechVentures Inc.",
      company: "Enterprise SaaS",
      rating: 5,
      metric: "400% ROI in 3 months"
    },
    {
      quote: "Our first analysis identified a $2M opportunity we were completely blind to. StratIQX pays for itself 100x over.",
      author: "Sarah Williams",
      title: "COO, Growth Dynamics",
      company: "E-commerce",
      rating: 5,
      metric: "$2M opportunity found"
    },
    {
      quote: "As a small business, we finally have access to enterprise-grade strategic intelligence we can afford. Game-changing.",
      author: "David Martinez",
      title: "Founder, Martinez Industries",
      company: "Manufacturing",
      rating: 5,
      metric: "60% faster decisions"
    }
  ]

  // FAQs
  const faqs = [
    {
      question: "How is StratIQX different from hiring consultants?",
      answer: "StratIQX delivers Big 3 consulting quality at 5% of the cost and 100x faster. While consultants charge $300-500K for 3-6 month engagements, we provide unlimited analyses for a fraction of the price with 4-hour delivery."
    },
    {
      question: "What kind of strategic questions can you analyze?",
      answer: "We handle all major strategic decisions: market entry/expansion, competitive analysis, M&A evaluation, pricing optimization, product strategy, growth planning, and risk assessment. If it's strategic, we can analyze it."
    },
    {
      question: "How quickly can I get started?",
      answer: "Immediately! Sign up takes 5 minutes, submit your first question right away, and receive your analysis within 4 hours."
    },
    {
      question: "Is my data secure?",
      answer: "Absolutely. We use bank-grade encryption, are SOC 2 compliant, and your data is never shared or used to train models. All analyses are covered by comprehensive NDAs."
    }
  ]

  // Comparison data
  const comparisonData = [
    { feature: 'Cost per Analysis', stratiqx: '$49 or $149', consultants: '$15,000-$50,000', inhouse: 'Your time (40+ hours)' },
    { feature: 'Delivery Time', stratiqx: '4 hours', consultants: '2-3 months', inhouse: '2-4 weeks' },
    { feature: 'Data Sources', stratiqx: '427+ premium sources', consultants: '10-20 sources', inhouse: '5-10 sources' },
    { feature: 'When You Need It', stratiqx: 'Pay per analysis', consultants: 'Annual contract', inhouse: 'Whenever you have time' },
    { feature: 'Annual Limit', stratiqx: 'Pay as you go', consultants: '1-2 projects', inhouse: 'Limited by capacity' }
  ]

  // Generate services from configuration - get all 4 AI orchestrator services in tier order
  const rawActiveServices = ServiceConfigUtils.getActiveServices()
  
  // Sort by tier level first, then convert to landing page format
  const sortedServices = rawActiveServices.sort((a, b) => {
    const tierOrder = { 'quick': 0, 'standard': 1, 'comprehensive': 2, 'enterprise': 3 }
    return (tierOrder[a.tierLevel] || 1) - (tierOrder[b.tierLevel] || 1)
  })
  
  const services = sortedServices.map(service => ({
    ...ServiceConfigUtils.toLandingPageFormat(service),
    id: service.id,
    tierLevel: service.tierLevel
  }))

  const qualities = [
    {
      icon: <Brain className="h-8 w-8 text-white" />,
      title: 'Research Strategy Mastery',
      description: 'Expert-designed frameworks that uncover hidden market dynamics'
    },
    {
      icon: <Zap className="h-8 w-8 text-white" />,
      title: 'AI-Powered Speed',
      description: '10x faster analysis with advanced AI orchestration'
    },
    {
      icon: <Shield className="h-8 w-8 text-white" />,
      title: 'Proven Methodology',
      description: 'Battle-tested frameworks ensure consistent quality'
    }
  ]

  // Auto-rotate testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length)
    }, 5000)
    return () => clearInterval(timer)
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Navigation */}
      <MainNavigation variant="full" />

      {/* Enhanced Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-cyan-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse animation-delay-2000"></div>
          <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse animation-delay-4000"></div>
        </div>

        <div className="container mx-auto max-w-6xl relative z-10">
          {/* Value Proposition Badge */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center bg-cyan-900/20 border border-cyan-500/30 rounded-full px-4 py-2">
              <Target className="h-5 w-5 text-cyan-400 mr-2" />
              <span className="text-cyan-400 font-semibold">Get Answers to Your Biggest Business Questions</span>
            </div>
          </div>

          {/* Main Hero Content */}
          <div className="text-center mb-12">
            <div className="text-6xl md:text-7xl font-bold text-white mb-4">
              Strat<span className="text-blue-400">IQ</span><span className="text-cyan-400">X</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 bg-gradient-to-r from-white via-cyan-300 to-blue-300 bg-clip-text text-transparent">
              Strategic Intelligence for Your Business
            </h1>
            <p className="text-xl md:text-2xl text-blue-200 mb-8 font-medium">
              Get answers to your biggest business questions in 4 hours, not months
            </p>

            {/* Business Questions - Enhanced Cards */}
            <div className="grid md:grid-cols-3 gap-4 mb-10 max-w-5xl mx-auto">
              <div className="group relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-xl p-5 border border-blue-700/30 hover:border-cyan-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400/30 to-blue-400/30 flex items-center justify-center text-cyan-300 group-hover:text-cyan-200 group-hover:from-cyan-400/40 group-hover:to-blue-400/40 transition-colors shadow-lg shadow-cyan-500/20">
                    <Target className="h-6 w-6 drop-shadow-[0_0_6px_rgba(103,232,249,0.3)]" />
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed group-hover:text-white transition-colors">
                    Should you expand to a second location?
                  </p>
                </div>
              </div>
              <div className="group relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-xl p-5 border border-blue-700/30 hover:border-cyan-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400/30 to-blue-400/30 flex items-center justify-center text-cyan-300 group-hover:text-cyan-200 group-hover:from-cyan-400/40 group-hover:to-blue-400/40 transition-colors shadow-lg shadow-cyan-500/20">
                    <TrendingUp className="h-6 w-6 drop-shadow-[0_0_6px_rgba(103,232,249,0.3)]" />
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed group-hover:text-white transition-colors">
                    Which market should you enter next?
                  </p>
                </div>
              </div>
              <div className="group relative bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-sm rounded-xl p-5 border border-blue-700/30 hover:border-cyan-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-cyan-500/20">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-400/30 to-blue-400/30 flex items-center justify-center text-cyan-300 group-hover:text-cyan-200 group-hover:from-cyan-400/40 group-hover:to-blue-400/40 transition-colors shadow-lg shadow-cyan-500/20">
                    <Users className="h-6 w-6 drop-shadow-[0_0_6px_rgba(103,232,249,0.3)]" />
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed group-hover:text-white transition-colors">
                    How should you respond to new competition?
                  </p>
                </div>
              </div>
            </div>

            {/* Pricing Callout - Enhanced Badge */}
            <div className="flex justify-center mb-10">
              <div className="inline-flex items-center gap-3 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-full px-6 py-3 backdrop-blur-sm hover:border-cyan-500/50 transition-all duration-300">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-cyan-400" />
                  <span className="text-lg font-bold text-white">$49-$149</span>
                </div>
                <div className="w-px h-6 bg-cyan-500/30" />
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-cyan-400" />
                  <span className="text-gray-300">4-hour delivery</span>
                </div>
                <div className="w-px h-6 bg-cyan-500/30" />
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-cyan-400" />
                  <span className="text-gray-300">No subscription</span>
                </div>
              </div>
            </div>
          </div>

          {/* Animated Stats - Enhanced with Icons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12 max-w-5xl mx-auto">
            {stats.map((stat, index) => (
              <div
                key={index}
                ref={stat.ref}
                className="group relative bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-md rounded-2xl p-6 border border-blue-700/30 hover:border-cyan-500/60 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20 overflow-hidden"
              >
                {/* Gradient background on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300`} />

                <div className="relative z-10">
                  {/* Icon */}
                  <div className="flex justify-center mb-3">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} opacity-30 group-hover:opacity-40 flex items-center justify-center transition-all duration-300 shadow-lg`}>
                      <div className="text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)] group-hover:scale-110 transition-transform duration-300">
                        {stat.icon}
                      </div>
                    </div>
                  </div>

                  {/* Number */}
                  <div className="text-4xl md:text-5xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-300 to-blue-300 mb-2 text-center drop-shadow-[0_0_10px_rgba(103,232,249,0.3)]">
                    {stat.prefix || ''}{stat.number}{stat.suffix || ''}
                  </div>

                  {/* Label */}
                  <div className="text-xs md:text-sm text-gray-300 uppercase tracking-wider text-center font-semibold group-hover:text-gray-200 transition-colors">
                    {stat.label}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Buttons - Enhanced Hierarchy */}
          <div className="flex flex-col items-center gap-6 mb-10">
            {/* Primary CTA - Most Prominent */}
            <Button
              onClick={handleGetStarted}
              size="lg"
              className="group relative bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-10 py-5 text-xl font-bold shadow-2xl shadow-cyan-500/50 hover:shadow-cyan-500/70 transition-all duration-300 hover:scale-105 rounded-xl"
            >
              <span className="relative z-10 flex items-center">
                Get Your First Analysis
                <ArrowRight className="ml-3 h-6 w-6 group-hover:translate-x-1 transition-transform" />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-400 to-blue-400 opacity-0 group-hover:opacity-20 rounded-xl transition-opacity duration-300" />
            </Button>

            {/* Secondary CTAs - Less Prominent */}
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              <Button
                onClick={handleCalculateROI}
                size="lg"
                variant="outline"
                className="bg-slate-800/50 border-2 border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500 px-6 py-3 rounded-xl transition-all duration-300 hover:scale-105"
              >
                <Calculator className="mr-2 h-5 w-5" />
                Calculate Your ROI
              </Button>
              <Button
                onClick={handleWatchDemo}
                size="lg"
                variant="ghost"
                className="text-gray-300 hover:text-white hover:bg-white/5 px-6 py-3 rounded-xl transition-all duration-300"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch 2-Min Demo
              </Button>
            </div>
          </div>

          {/* Trust Badges - Enhanced */}
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-400">
            <div className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
              <Shield className="h-4 w-4 text-cyan-400" />
              <span>SOC 2 Compliant</span>
            </div>
            <div className="w-px h-4 bg-gray-600" />
            <div className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
              <Award className="h-4 w-4 text-cyan-400" />
              <span>30-Day Guarantee</span>
            </div>
            <div className="w-px h-4 bg-gray-600" />
            <div className="flex items-center gap-2 hover:text-cyan-400 transition-colors">
              <Clock className="h-4 w-4 text-cyan-400" />
              <span>4-Hour Delivery</span>
            </div>
          </div>
        </div>
      </section>

      {/* Client Logos Section */}
      <section className="py-12 px-4 bg-slate-800/30 border-y border-blue-800/30">
        <div className="container mx-auto max-w-6xl">
          <p className="text-center text-gray-400 mb-8 uppercase tracking-wider text-sm">
            Trusted by Industry Leaders
          </p>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-8 items-center">
            {clientLogos.map((client, index) => (
              <div key={index} className="text-center opacity-60 hover:opacity-100 transition-opacity">
                <div className="bg-slate-700/50 rounded-lg p-4 border border-blue-800/30">
                  <Building2 className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-400">{client.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROI Comparison Section */}
      <section className="py-20 px-4 bg-gradient-to-b from-slate-900 to-slate-800">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              The StratIQX Advantage
            </h2>
            <p className="text-lg text-gray-300">
              See how we stack up against traditional alternatives
            </p>
          </div>

          <Card className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-blue-600 to-cyan-600">
                    <th className="px-6 py-4 text-left text-white font-semibold">Feature</th>
                    <th className="px-6 py-4 text-center text-white font-semibold">
                      <Sparkles className="h-5 w-5 inline mr-2" />
                      StratIQX
                    </th>
                    <th className="px-6 py-4 text-center text-white font-semibold opacity-75">
                      Traditional Consultants
                    </th>
                    <th className="px-6 py-4 text-center text-white font-semibold opacity-75">
                      DIY Research
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-slate-800/50' : 'bg-slate-700/30'}>
                      <td className="px-6 py-4 text-gray-300 font-medium">{row.feature}</td>
                      <td className="px-6 py-4 text-center">
                        <span className="text-cyan-400 font-bold text-lg">{row.stratiqx}</span>
                      </td>
                      <td className="px-6 py-4 text-center text-gray-400">{row.consultants}</td>
                      <td className="px-6 py-4 text-center text-gray-400">{row.inhouse}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="text-center mt-8">
            <Button
              onClick={handleGetStarted}
              size="lg"
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500"
            >
              Start Saving 95% on Strategic Analysis
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Real Results from Real Leaders
            </h2>
            <p className="text-lg text-gray-300">
              See what our clients are achieving with StratIQX
            </p>
          </div>

          <Card className="bg-gradient-to-br from-cyan-900/80 via-blue-900/80 to-purple-900/80 border-cyan-500/50 backdrop-blur-sm shadow-2xl">
            <CardContent className="p-8 md:p-12 relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-tr from-cyan-500/10 via-transparent to-purple-500/10 pointer-events-none" />
              
              <div className="relative z-10 flex items-start mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-6 w-6 text-yellow-400 fill-current drop-shadow-lg" />
                ))}
                <span className="ml-auto bg-gradient-to-r from-green-600/30 to-emerald-600/30 border border-green-400/50 backdrop-blur-sm rounded-full px-4 py-2">
                  <span className="text-green-300 font-bold text-sm">
                    {testimonials[activeTestimonial].metric}
                  </span>
                </span>
              </div>

              <blockquote className="relative z-10 text-2xl text-white mb-8 italic leading-relaxed font-light">
                "{testimonials[activeTestimonial].quote}"
              </blockquote>

              <div className="relative z-10 flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold text-lg">{testimonials[activeTestimonial].author}</p>
                  <p className="text-cyan-300 font-medium">{testimonials[activeTestimonial].title}</p>
                  <p className="text-blue-200/60 text-sm">{testimonials[activeTestimonial].company}</p>
                </div>

                <div className="flex space-x-2">
                  {testimonials.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveTestimonial(index)}
                      className={`w-2 h-2 rounded-full transition-all ${
                        activeTestimonial === index 
                          ? 'w-8 bg-cyan-400 shadow-lg shadow-cyan-400/50' 
                          : 'bg-gray-500 hover:bg-gray-400'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Services Section - Improved */}
      <section id="services" className="py-20 px-4 bg-slate-800/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              AI Intelligence Service Tiers
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-4">
              Choose the intelligence depth that matches your business needs. From quick market insights to comprehensive strategic analysis - all powered by our AI orchestrator.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-6">
              <span className="flex items-center">
                <Zap className="h-4 w-4 text-emerald-400 mr-1" />
                Quick (15-min processing)
              </span>
              <ArrowRight className="h-4 w-4" />
              <span className="flex items-center">
                <BarChart3 className="h-4 w-4 text-blue-400 mr-1" />
                Standard (30-min processing)
              </span>
              <ArrowRight className="h-4 w-4" />
              <span className="flex items-center">
                <Target className="h-4 w-4 text-purple-400 mr-1" />
                Comprehensive (45-min processing)
              </span>
              <ArrowRight className="h-4 w-4" />
              <span className="flex items-center">
                <Award className="h-4 w-4 text-amber-400 mr-1" />
                Enterprise (60-min processing)
              </span>
            </div>
          </div>

          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-6 lg:gap-8">
            {services.map((service, index) => (
              <Card key={index} className={`bg-slate-800 border-blue-800/30 transition-all duration-300 hover:scale-105 group relative ${
                service.comingSoon ? 'opacity-75' : 'hover:border-cyan-500/50 cursor-pointer'
              }`}
              onClick={() => !service.comingSoon && handleGetStarted()}
              >
                <CardContent className="p-6">
                  {/* Tier Badge - always show on landing page */}
                  {service.tierLevel && (() => {
                    const tierConfig = getTierBadgeConfig(service.tierLevel)
                    if (!tierConfig) return null
                    
                    return (
                      <div className={`absolute top-0 right-0 ${tierConfig.bgClass} text-white px-3 py-1 text-xs font-semibold rounded-bl-lg flex items-center gap-1 z-10`}>
                        {tierConfig.icon}
                        {tierConfig.label}
                      </div>
                    )
                  })()}

                  {service.comingSoon && (
                    <div className="absolute top-2 right-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      Coming Soon
                    </div>
                  )}
                  {service.popular && !service.comingSoon && (
                    <div className="absolute top-2 right-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center">
                      <Star className="h-3 w-3 mr-1" />
                      Popular
                    </div>
                  )}
                  
                  <div className="bg-gradient-to-r from-blue-600 to-cyan-600 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                    {service.icon}
                  </div>
                  
                  <h3 className="text-lg font-semibold text-white mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-300 mb-3 leading-relaxed text-sm">
                    {service.description.split('.')[0] + '.'}
                  </p>
                  <div className={`font-semibold mb-4 ${
                    service.price.includes('Free') ? 'text-green-400' : 'text-cyan-400'
                  }`}>
                    {(() => {
                      const priceFormatted = formatPriceWithSmallDecimals(service.price)
                      if (typeof priceFormatted === 'string') {
                        return <span className="text-lg">{priceFormatted}</span>
                      }
                      return (
                        <>
                          <span className="text-lg">{priceFormatted.dollars}</span>
                          <span className="text-sm">{priceFormatted.cents}</span>
                          <span className="text-sm ml-1">{priceFormatted.currency}</span>
                        </>
                      )
                    })()}
                  </div>
                  
                  <ul className="space-y-1.5 mb-4">
                    {service.features.slice(0, 2).map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start text-gray-300 text-xs">
                        <CheckCircle className="h-3 w-3 text-cyan-400 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="leading-tight">{feature}</span>
                      </li>
                    ))}
                    <li className="text-xs text-gray-400 pl-5">
                      +{service.features.length - 2} more features
                    </li>
                  </ul>

                  {!service.comingSoon && (
                    <div className="text-cyan-400 font-semibold text-sm flex items-center group-hover:text-cyan-300">
                      Learn more
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-2 transition-transform" />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button
              onClick={() => navigate('/pricing')}
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
            >
              View All Services
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-slate-900">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-300">
              Everything you need to know about StratIQX
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="bg-slate-800/80 border-blue-800/30 backdrop-blur-sm">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left"
                >
                  <h3 className="text-lg font-semibold text-white pr-4">{faq.question}</h3>
                  {expandedFAQ === index ? (
                    <ChevronUp className="h-5 w-5 text-cyan-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {expandedFAQ === index && (
                  <CardContent className="px-6 pb-4">
                    <p className="text-gray-300">{faq.answer}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why StratIQX Section - Simplified */}
      <section id="approach" className="py-20 px-4 bg-gradient-to-b from-slate-800 to-slate-900">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Why Business Owners Choose StratIQX
            </h2>
            <p className="text-lg text-gray-300">
              The triple advantage that delivers superior results
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {qualities.map((quality, index) => (
              <div key={index} className="text-center group">
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                  {quality.icon}
                </div>
                <h3 className="text-xl font-semibold text-cyan-400 mb-4">
                  {quality.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {quality.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-900 to-cyan-900">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Get Answers to Your Business Questions?
          </h2>
          <p className="text-xl text-cyan-100 mb-6">
            Join hundreds of business owners making better decisions in hours, not months
          </p>
          <p className="text-lg text-cyan-400 font-semibold mb-8">
            Starting at $49 • No subscription • 30-day guarantee
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button
              onClick={handleGetStarted}
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 text-lg"
            >
              Get Started Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={handleViewPricing}
              size="lg"
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
            >
              View Pricing
              <DollarSign className="ml-2 h-5 w-5" />
            </Button>
          </div>

          <div className="flex items-center justify-center space-x-6 text-white/80 text-sm">
            <div className="flex items-center">
              <CheckCircle className="h-4 w-4 mr-2" />
              No credit card required
            </div>
            <div className="flex items-center">
              <Timer className="h-4 w-4 mr-2" />
              Setup in 5 minutes
            </div>
            <div className="flex items-center">
              <Shield className="h-4 w-4 mr-2" />
              30-day guarantee
            </div>
          </div>
        </div>
      </section>

      {/* Floating CTA Button */}
      <div className="fixed bottom-8 right-8 z-50">
        <Button
          onClick={handleGetStarted}
          className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white px-6 py-3 rounded-full shadow-2xl flex items-center group"
        >
          <Sparkles className="h-5 w-5 mr-2 group-hover:rotate-12 transition-transform" />
          Get Started
        </Button>
      </div>

      {/* Video Modal with Benefits Carousel */}
      {showVideo && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 md:p-8" onClick={() => setShowVideo(false)}>
          <div className="w-full max-w-5xl max-h-[90vh] flex flex-col">
            <Card className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 border-cyan-500/30 w-full shadow-2xl my-4 flex flex-col" onClick={(e) => e.stopPropagation()}>
            <CardContent className="p-0 overflow-hidden flex flex-col flex-1">
              {/* Header */}
              <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-4 relative">
                <button
                  onClick={() => setShowVideo(false)}
                  className="absolute top-2 right-2 text-white/80 hover:text-white transition-colors bg-black/20 hover:bg-black/40 rounded-full p-1.5 z-10"
                >
                  <X className="h-5 w-5" />
                </button>
                <div className="flex items-center">
                  <div className="bg-white/20 backdrop-blur-sm rounded-full p-2 mr-3">
                    <Video className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-0.5">See StratIQX in Action</h3>
                    <p className="text-cyan-100 text-sm">Demo video coming soon</p>
                  </div>
                </div>
              </div>

              {/* Scrollable Content Area */}
              <div className="flex-1 overflow-y-auto p-6">
                {/* Coming Soon Badge */}
                <div className="text-center mb-6">
                  <div className="inline-flex items-center bg-amber-900/20 border border-amber-500/30 rounded-lg px-3 py-1.5 mb-3">
                    <Sparkles className="h-3 w-3 text-amber-400 mr-2 animate-pulse" />
                    <span className="text-amber-300 font-medium text-sm">Demo Coming Soon</span>
                  </div>
                  <p className="text-gray-300 text-sm">Discover what makes StratIQX revolutionary:</p>
                </div>

                {/* Animated Benefits Grid */}
                <div className="grid md:grid-cols-2 gap-4">
                  {[
                    {
                      icon: <Clock className="h-6 w-6" />,
                      title: "4-Hour Delivery",
                      description: "Get comprehensive analysis faster than a typical consultant meeting",
                      color: "from-cyan-600 to-blue-600"
                    },
                    {
                      icon: <DollarSign className="h-6 w-6" />,
                      title: "95% Cost Savings",
                      description: "Big 3 consulting quality at 5% of the price",
                      color: "from-green-600 to-emerald-600"
                    },
                    {
                      icon: <Database className="h-6 w-6" />,
                      title: "427+ Data Sources",
                      description: "AI analyzes more sources than an entire consulting team",
                      color: "from-purple-600 to-pink-600"
                    },
                    {
                      icon: <Shield className="h-6 w-6" />,
                      title: "Expert Validation",
                      description: "Every insight verified by strategic intelligence experts",
                      color: "from-orange-600 to-red-600"
                    }
                  ].map((benefit, index) => (
                    <div
                      key={index}
                      className="group relative bg-slate-800/50 backdrop-blur-sm rounded-lg p-6 border border-blue-800/30 hover:border-cyan-500/50 transition-all hover:scale-105"
                      style={{ animationDelay: `${index * 100}ms` }}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-r ${benefit.color} opacity-5 group-hover:opacity-10 rounded-lg transition-opacity`} />
                      <div className={`bg-gradient-to-r ${benefit.color} w-12 h-12 rounded-lg flex items-center justify-center mb-4 text-white`}>
                        {benefit.icon}
                      </div>
                      <h4 className="text-lg font-semibold text-white mb-2">{benefit.title}</h4>
                      <p className="text-gray-400">{benefit.description}</p>
                    </div>
                  ))}
                </div>

                {/* Email Capture for Video Launch */}
                <div className="bg-gradient-to-r from-blue-900/50 to-cyan-900/50 rounded-lg p-6 border border-cyan-500/30">
                  <div className="text-center">
                    <h4 className="text-xl font-bold text-white mb-2">
                      Be First to See the Demo
                    </h4>
                    <p className="text-gray-300 mb-4">
                      Get notified when our 2-minute platform walkthrough is ready
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                      <input
                        type="email"
                        placeholder="Enter your email"
                        className="flex-1 px-4 py-2 bg-slate-800/50 border border-blue-700/30 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors"
                      />
                      <Button className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white">
                        <Bell className="h-4 w-4 mr-2" />
                        Notify Me
                      </Button>
                    </div>
                  </div>
                </div>

              </div>

              {/* Fixed Footer */}
              <div className="border-t border-slate-700 bg-slate-800/50 p-4">
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => {
                      setShowVideo(false)
                      handleGetStarted()
                    }}
                    className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white"
                    size="default"
                  >
                    Start Free Analysis
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                  <Button
                    onClick={() => {
                      setShowVideo(false)
                      handleCalculateROI()
                    }}
                    variant="outline"
                    className="bg-transparent border border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
                    size="default"
                  >
                    <Calculator className="mr-2 h-4 w-4" />
                    Calculate ROI
                  </Button>
                </div>
                <p className="text-center text-gray-400 text-xs mt-2">
                  Experience AI-powered strategic intelligence
                </p>
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      )}

      {/* Footer */}
      <MainFooter variant="full" />
    </div>
  )
}